import { ThemeOptions } from "@mui/material";
import { deepmerge } from "@mui/utils";
import {
  createSharedTheme,
  fontFamily,
  kaizenThemeOptions,
  palette as sharedPalette,
  paletteVar,
  typography as sharedTypography,
} from "@platform/design-system";
import { CSSProperties } from "react";

import { kaizenDarkSchemeOptions } from "./dark-scheme";

// Token names reused by more than one palette entry below.
const TEXT_SECONDARY_TOKEN = "text-secondary";
const ERROR_MAIN_TOKEN = "error-main";

// This theme and the Kaizen palette name opposite colours: brand purple is
// `secondary` here and `primary` there, supporting teal the other way round. So
// each var below is paired with the token holding the same colour, not the one
// holding the same name — `secondary-main` would render a purple link teal.
const brandSupportingVar = paletteVar(
  "secondary-main",
  sharedPalette.primary.medium,
);
const textPrimaryVar = paletteVar("text-primary", sharedPalette.text.primary);
const textSecondaryVar = paletteVar(
  TEXT_SECONDARY_TOKEN,
  sharedPalette.text.secondary,
);
const errorMainVar = paletteVar(
  ERROR_MAIN_TOKEN,
  sharedPalette.primary.required,
);

const multiLineEllipsis = (lineClamp: string): CSSProperties => ({
  overflow: "hidden",
  textOverflow: "ellipsis",
  display: "-webkit-box",
  WebkitLineClamp: lineClamp,
  WebkitBoxOrient: "vertical",
  wordWrap: "break-word",
});

declare module "@mui/material/styles" {
  interface Palette {
    ui?: {
      success?: string;
      info?: string;
      error?: string;
    };
    opaque?: PaletteColor;
    errorbox?: {
      background?: string;
      main?: string;
    };
    rules?: {
      normal?: string;
      done?: string;
      wrong?: string;
    };
    alert?: {
      success?: {
        dark?: string;
      };
    };
  }
  interface PaletteOptions {
    ui?: {
      success?: string;
      info?: string;
      error?: string;
    };
    opaque?: SimplePaletteColorOptions;
    errorbox?: {
      background?: string;
      main?: string;
    };
    rules?: {
      normal?: string;
      done?: string;
      wrong?: string;
    };
    alert?: {
      success?: {
        dark?: string;
      };
    };
    apple_green?: string;
    marigold?: string;
    pumpkin?: string;
  }
  interface PaletteColor {
    apple_green?: string;
    marigold?: string;
    pumpkin?: string;
  }
  interface SimplePaletteColorOptions {
    apple_green?: string;
    marigold?: string;
    pumpkin?: string;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    banner: true;
  }
}

// The standard MUI roles (primary, secondary, warning, error, text, background)
// are tokenized by the Kaizen theme's `paletteFor`. These app-specific
// namespaces are not, so each value routes through `paletteVar` to the token
// holding its colour: the fallback keeps the current literal (so a theme
// without the tokens renders unchanged), and the Kaizen theme resolves the
// token and follows the active scheme. Values map by colour, not by name.
const palette = {
  ui: {
    success: paletteVar("success-main", "#bcda81"),
    // Orange despite the name — it's an attention colour, so it reads the
    // warning token rather than the accent (info) one.
    info: paletteVar("warning-main", "#E69238"),
    error: paletteVar(ERROR_MAIN_TOKEN, "#df4c32"),
  },
  warning: {
    main: "#E69238",
    dark: "#996125",
    light: "#EEB67A",
    contrastText: "#FFFFFF",
  },
  secondary: {
    main: "#832A80",
    // App-local accents with no design token yet; kept literal pending design.
    apple_green: "#8eb935",
    marigold: "#f3a61b",
    pumpkin: "#f1711e",
  },
  opaque: {
    // A `main` slot: MUI builds a colour variant for it and runs `alpha()` on
    // the value, which rejects a `var()`, so this stays a literal.
    main: "#757575",
  },
  errorbox: {
    background: paletteVar("background-default", "#f7f7f7"),
    // Literal for the same reason as `opaque.main` above.
    main: "#df4c32",
  },
  rules: {
    normal: paletteVar(TEXT_SECONDARY_TOKEN, "#666666"),
    done: paletteVar("primary-main", "#832A80"),
    wrong: paletteVar("warning-main", "#E69238"),
  },
  alert: {
    success: {
      dark: paletteVar("success-dark", "#1E4620"),
    },
  },
  dropzone: {
    lineSoft: paletteVar("divider", "#e0e0e0"),
    lineHard: paletteVar("action-disabled", "#9e9e9e"),
    // Literal: census-components runs `darken()`/`hexToRgb()` on this in JS,
    // which reject a `var()`.
    error: "#D32F2F",
  },
  error: {
    main: "#DF4C32",
    dark: "#862E1E",
    light: "#EC9484",
    contrastText: "#FFFFFF",
  },
};

const typography = {
  subtitle1: {
    fontFamily,
    color: textSecondaryVar,
    letterSpacing: 0.7,
    "&.multiLineEllipsis": multiLineEllipsis("1"),
  },
  subtitle2: {
    fontFamily,
    color: textSecondaryVar,
    letterSpacing: 0.4,
    fontSize: 12,
    fontWeight: 500,
    "&.multiLineEllipsis": multiLineEllipsis("1"),
  },
};

const newThemeOptions: ThemeOptions = {
  palette,
  typography,
};

const components = {
  MuiContainer: {
    styleOverrides: {
      root: {
        "@media (min-width: 1400px)": {
          maxWidth: 1400,
        },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        fontFamily,
        fontSize: 14,
        fontWeight: 900,
        lineHeight: "130%",
        letterSpacing: 0.7,
        color: textPrimaryVar,
      },
      root: {
        ...sharedTypography.small,
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        "&.detail-page-tabs": {
          minHeight: 0,
          ".MuiButtonBase-root": {
            minHeight: 48,
            color: brandSupportingVar,
            padding: "0 1rem",
            "&:not(:first-of-type)": {
              marginLeft: 0,
            },
            "&:not(.Mui-selected) .error": {
              color: errorMainVar,
            },
          },
          ".MuiTabs-indicator": {
            backgroundColor: brandSupportingVar,
            height: 2,
          },
          ".MuiTypography-root": {
            color: brandSupportingVar,
            padding: "0 1rem",
          },
          "& .Mui-selected *": {
            fontWeight: 800,
          },
        },
        ".MuiButtonBase-root": {
          padding: 0,
          "&:not(:first-of-type)": {
            marginLeft: 30,
          },
          fontSize: 16,
          fontWeight: 400,
          "&:not(.Mui-selected)": {
            color: textPrimaryVar,
          },
        },
      },
      indicator: {
        height: 5,
        borderRadius: "6.25rem",
      },
    },
  },
};

const finalThemeOptions = deepmerge(newThemeOptions, { components });

export const theme = createSharedTheme(finalThemeOptions);

// The design system layered over the app's own theme rather than replacing it,
// so the app's custom variants and palette namespaces keep resolving while
// design's tokens supply the values. Merging options rather than finished
// themes means `createTheme` derives light/dark/contrastText once, from the
// combined palette — merging built themes would leave the old derived shades
// sitting under the new `main`.
//
// Mounted behind the `kaizen-theme` flag; see `ThemeSelector` in
// `Providers.js`. `theme-contract.test.ts` asserts this drops nothing the app
// depends on.
// The dark palette comes from design's tokens (the Kaizen theme's
// `colorSchemes.dark` / `paletteFor(dark)`); `kaizenDarkSchemeOptions` adds the
// app-layer dark wiring tokens can't reach (see `dark-scheme.ts`). Composed only
// into the Kaizen theme, active only in the dark scheme.
export const kaizenComposedTheme = createSharedTheme(
  deepmerge(
    deepmerge(finalThemeOptions, kaizenThemeOptions),
    kaizenDarkSchemeOptions,
  ),
);
